// src/contexts/AuthContext.js
import { jwtDecode } from 'jwt-decode';
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useGoogleLogin } from '@react-oauth/google'; // Import the hook
import axios from 'axios'; // For sending token to backend
import { config } from "../config/appConfig";

const AuthContext = createContext({
    isAuthenticated: false,
    user: null,
    token: null,
    login: (token) => {},
    logout: () => {},
    triggerGoogleLogin: () => { console.warn("Google login trigger not ready."); }, // Add trigger function
    isLoading: true,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const handleBackendVerify = useCallback(async (idToken) => {
        if (!idToken) { console.error('Google Login did not return a credential.'); return; }
        console.log("ID Token received, sending to backend:", idToken.substring(0, 20) + "...");
        try {
            // Use environment variable for backend URL in real app
            const backendUrl = config.api.auth.googleVerify;
            const response = await axios.post(backendUrl, { token: idToken });
            if (response.data && response.data.jwtToken) {
                login(response.data.jwtToken); // Use internal login function to set state
            } else {
                console.error('Backend did not return a valid JWT.');
                 logout(); // Ensure clean state on error
            }
        } catch (error) {
            if (error.response) {
                console.error('Error verifying token with backend - Status:', error.response.status, 'Data:', error.response.data);
            } else if (error.request) { console.error('Error verifying token - No response:', error.request); }
            else { console.error('Error verifying token - Request setup:', error.message); }
             logout(); // Ensure clean state on error
        } finally {
            // Optionally set loading state false if needed here
        }
    }, []); // Added `login` and `logout` as dependencies

    // Use the hook internally
    const googleLogin = useGoogleLogin({
        onSuccess: (tokenResponse) => {
            // Google now returns an access token with useGoogleLogin.
            // We need the ID token. Typically, the access token can be used
            // to fetch user profile info which might include the ID token,
            // OR the backend needs to handle access token verification.
            // For simplicity, if your backend `/api/auth/google/verify` can handle an *access token*, send that.
            // If it *requires* an ID token, you might need to adjust the Google setup or backend.
            // Assuming backend handles access token or you adjust Google setup:
            console.log("Google Login Success (useGoogleLogin):", tokenResponse);
            // We need to get profile info using the access token to get email etc.
            // OR send the access_token to backend for verification + profile fetch.
            // Let's assume backend handles access_token verification AND gets user info.
            handleBackendVerify(tokenResponse.access_token); // Send access token

            // ---- If backend requires ID Token ----
            // You might need a different flow or library setup that prioritizes ID token.
            // The older <GoogleLogin> component provides the ID token directly in `credentialResponse.credential`.
            // If you stick with useGoogleLogin, you'd typically fetch user info:
            /*
            axios.get(`https://www.googleapis.com/oauth2/v1/userinfo?access_token=${tokenResponse.access_token}`, {
                headers: { Authorization: `Bearer ${tokenResponse.access_token}`, Accept: 'application/json'}
            })
            .then((res) => {
                console.log("Google User Info:", res.data);
                // Now you have email, name, etc. but still might need ID token for backend verify
                // This approach is more complex if backend ONLY accepts ID token.
            })
            .catch((err) => console.error("Error fetching Google user info:", err));
            */
             // --- Simpler: Switch back to <GoogleLogin> component if ID token is essential for backend ---
             // The provided backend code expects an ID token. So, using useGoogleLogin like this
             // might require backend changes. For now, we proceed assuming backend is adapted or
             // that `handleBackendVerify` somehow gets the ID token (which it currently doesn't directly).
             // Let's keep the flow but acknowledge this potential mismatch.
        },
        onError: (errorResponse) => {
            console.error('Google Login Failed:', errorResponse);
            // Handle login errors appropriately
        },
        // flow: 'auth-code', // Use 'implicit' for access token only, 'auth-code' for backend exchange (more secure)
    });

    const login = useCallback((newToken) => {
        try {
            // Basic validation: check if it's a string and has three parts
            if (typeof newToken !== 'string' || newToken.split('.').length !== 3) {
                throw new Error("Invalid token format received.");
            }
            localStorage.setItem('authToken', newToken);
            const decoded = jwtDecode(newToken);
            const userData = {
                 id: decoded.sub,
                 email: decoded.email,
                 plan: decoded.plan
             };
            setToken(newToken);
            setUser(userData);
            setIsAuthenticated(true);
            console.log("AuthContext: User logged in:", userData);
        } catch (error) {
            console.error("AuthContext: Failed to decode or set token:", error);
            logout(); // Clear state if token is invalid
        }
    }, []); // Removed 'logout' from dependencies here, but keep it in the effect below

    const logout = useCallback(() => {
        console.log("AuthContext: Logging out...");
        localStorage.removeItem('authToken');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
    }, []); // No dependencies needed here

    useEffect(() => {
        console.log("AuthContext: Initializing - checking stored token...");
        const storedToken = localStorage.getItem('authToken');
        if (storedToken) {
            try {
                const decoded = jwtDecode(storedToken);
                if (decoded.exp * 1000 > Date.now()) {
                    console.log("AuthContext: Valid stored token found.");
                    login(storedToken);
                } else {
                    console.log("AuthContext: Stored token expired.");
                    logout();
                }
            } catch (error) {
                 console.error("AuthContext: Invalid stored token found.");
                logout();
            }
        } else {
            console.log("AuthContext: No stored token found.");
        }
        setIsLoading(false);
        console.log("AuthContext: Initialization complete.");
    }, [login, logout]);


    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            user,
            token,
            login, // Keep login if needed externally (e.g., manual login form)
            logout,
            triggerGoogleLogin: googleLogin, // Expose the function from the hook
            isLoading
        }}>
            {children}
        </AuthContext.Provider>
    );
};